<template>
  <div :class="appClasses"> 
    <component v-if="useDynamicComponents" :is="layout">
      <template #desktop-plan-information>
        <div class="relative">
          <div v-if="!isSmall" class="w-full md:max-w-lg">
            <internet-5g-offer-card v-if="isInternet5gFlow" />
            <we-selected-plan-card v-else />
          </div>
        </div>
      </template>

      <template #content-mobile>
        <div class="content-mobile absolute pb-32 w-full px-6" :style="contentPositionStyle">
          <div v-if="useSmallSelectedPlan" class="w-full -translate-y-10">
            <internet-5g-offer-card v-if="isInternet5gFlow" />
            <we-selected-plan-card v-else />
          </div>
          <router-view :key="$route.path"/>
        </div>
      </template>

      <template #content-desktop>
        <div class="relative w-full">
          <div v-if="isSmall" class="w-full md:max-w-lg mb-8">
            <internet-5g-offer-card v-if="isInternet5gFlow" />
            <we-selected-plan-card v-else />
          </div>
          <div class="content-desktop-router-view">
            <router-view :key="$route.path" />
          </div>
        </div>
      </template>
    </component>

    <router-view :key="$route.path" v-else/>

    <!-- Modal Global de Verificação de Contrato -->
    <we-contract-verification-modal />
  </div>
</template>

<script setup>
import '@/assets/tailwind.css'
import { computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useScreen, useSelectedPlanCard, useWeButton } from './composables'
import DesktopLayout from './layouts/DesktopLayout'
import MobileLayout from './layouts/MobileLayout'
import Internet5gOfferCard from './components/internet5g/Internet5gOfferCard'
import WeSelectedPlanCard from './components/WeSelectedPlanCard'
import WeContractVerificationModal from './components/WeContractVerificationModal'
import FingerprintJS from '@fingerprintjs/fingerprintjs'

const store = useStore()
const route = useRoute()
const router = useRouter()

// Initialize an agent at application startup.
const fpPromise = FingerprintJS.load()

const isBusiness = computed(() => route.query)

const isInternet5gFlow = computed(() => store.getters.getIs5g)

const appClasses = computed(() => {
  if (isInternet5gFlow.value) return 'font-nunito'

  return isBusiness.value ? 'business font-nunito-sans' : 'font-nunito'
})

const initialQuery = new URLSearchParams(window.location.search)

const hasNewPlanSelectionIntent = initialQuery.get('selected_city_id') || initialQuery.get('selected_plan_id') || initialQuery.get('type')

if (initialQuery.get('session_uuid') || initialQuery.get('sessionUuid') || initialQuery.get('session')) {
  store.commit('setIs5g', true)
} else if (!hasNewPlanSelectionIntent) {
  const raw5gDraft = localStorage.getItem('weclix_internet_5g_checkout')
  if (raw5gDraft) {
    try {
      JSON.parse(raw5gDraft)
      store.commit('setIs5g', true)
      store.dispatch('internet5g/hydrateDraft')
    } catch (e) {
      localStorage.removeItem('weclix_internet_5g_checkout')
    }
  }
}

//Screen setup
const { useSmallSelectedPlan } = useSelectedPlanCard()
const { setResizeListener, isSmall } = useScreen()
setResizeListener()

//Layout dynamic structure
const useDynamicComponents = computed(() => {
  if (route.name === 'Success') return false
  if (route.name === 'ScheduleYourService') return false
  if (route.name === 'Conclusion' && !isInternet5gFlow.value) return false
  return true
})

const layout = computed(() => {
  return isSmall.value ? MobileLayout : DesktopLayout
})

const contentPositionStyle = computed(() => {
  return useSmallSelectedPlan.value ? 'transform: translateY(-42px)' : 'margin-top: 24px'
})

watch(route, newRoute => {
  if (store.getters.getIs5g) {
    if (newRoute.name !== 'Documents') {
      store.dispatch('contractVerification/stopContractVerification')
    }

    if (newRoute.name === 'Availability' && (newRoute.query.session_uuid || newRoute.query.sessionUuid || newRoute.query.session)) {
      store.dispatch('internet5g/initializeFromQuery', newRoute.query)
    }

    const stepKey = store.getters['internet5g/getStepByRouteName'](newRoute.name)

    if (stepKey) {
      store.dispatch('internet5g/setActiveStep', stepKey)
    }

    return
  }

  if (newRoute.name === 'Confirm') {
    resetStates()
    store.dispatch('contractVerification/stopContractVerification')
    return
  }

  if (newRoute.name === 'Conclusion' || newRoute.name === 'ScheduleYourService') {
    store.dispatch('contractVerification/stopContractVerification')
    return
  }

  if (!store.getters.getActiveStep) {
    store.dispatch('setActiveStep', 'viability')
    return
  }

  const stepKey =
    store.getters.getStepByRouteName(newRoute.name)

  if (stepKey) {
    store.dispatch('setActiveStep', stepKey)
  }
})



//Stepper flow
const resetStates = () => {
  store.dispatch('resetSteps')
  store.dispatch('resetForm')
}

onMounted(async () => {      

  store.dispatch('setCepError', false);

  await router.isReady()

  const sessionUuidFromQuery = route.query.session_uuid || route.query.sessionUuid || route.query.session

  if (sessionUuidFromQuery) {
    await store.dispatch('internet5g/initialize5gSession', route.query)

    store.dispatch('contractVerification/stopContractVerification')
    store.dispatch('setLoading', false)

    const stepKey = store.getters['internet5g/getStepByRouteName'](route.name)

    if (stepKey) {
      store.dispatch('internet5g/setActiveStep', stepKey)
      router.push({ name: route.name, replace: true })
    } else {
      router.push({ name: 'Availability', replace: true })
    }

    return
  }

  if( route.params.id ) {

    try {
      const session = await store.dispatch('fetchSelectedPlanBySession', route.params.id)

      // store.dispatch('fetchPlansList', route.params.id)

      store.dispatch('inputFormObjectValues', {    
          name: session.prospect.name,
          email: session.prospect.email,
          phone: session.prospect.cellphone || session.prospect.phone,
          address_id: session.prospect.addresses?.address_id,
          address_name: session.prospect.addresses?.address_name,
          address_number: session.prospect.addresses?.number,
          address_complement: session.prospect.addresses?.complement,
          cpf: session.prospect.cpf,
          rg: session.prospect.rg,
          mother_name: session.prospect.mother_name,
          birth_date: session.prospect.birth_date,
          cnpj: session.prospect.cnpj,
          company_name: session.prospect.company_name,
          state_registration: session.prospect.state_registration,
          municipal_registration: session.prospect.municipal_registration,
          fantasy_name: session.prospect.fantasy_name,
          terms: false,
          payment_day_of_month: session.prospect.payment_day_of_month,
          code: session.promo_code?.code,
          day: session.installation.date,
          period: session.installation.period,
          home_type: session.installation.home_type,
          observation: session.installation.observation,
          serviceContactType: ''
      })

      store.dispatch('setActiveStep', session.status)

      if (session.status) {

        const currentStep = session.status === 'new' ? 'viability' : session.status;

        await store.dispatch('completeStepsUntil', currentStep)
        store.dispatch('setActiveStep', currentStep)

        const storedStatus = await store.dispatch('contractVerification/restoreContractStatusFromStorage')
        if (storedStatus?.status === 'under_analysis') {
          const sessionUuid = localStorage.getItem('session_uuid')
          if (sessionUuid) {
            store.dispatch('contractVerification/startContractVerification', sessionUuid)
          }
        }

        return router.push({
          name: store.state.stepper.stepper[currentStep].routeName,
          replace: true
        })

      } else {
        return router.push({ name: 'Availability', replace: true })
      }
    } catch (e) {
      localStorage.clear()
      console.log('houve um erro->', e)
    }

  } else {

    const selectedCity = route.query.selected_city_id
    const selectedPlan = route.query.selected_plan_id
    const selectedPlanType = route.query.type
    const target = route.query.target
    const promoCode = route.query.promo ?? '';
    const affiliate = route.query.a ?? '';

    const hasNewPlanSelectionIntent = selectedCity || selectedPlan || selectedPlanType

    const raw5gDraft = localStorage.getItem('weclix_internet_5g_checkout')
    if (raw5gDraft && !hasNewPlanSelectionIntent) {
      try {
        const { campaignParams, activeStep } = JSON.parse(raw5gDraft)
        const sessionUuid = campaignParams?.session_uuid || campaignParams?.sessionUuid || campaignParams?.session || localStorage.getItem('session_uuid')

        if (sessionUuid) {
          await store.dispatch('internet5g/initialize5gSession', campaignParams)
          store.commit('setIs5g', true)
          store.dispatch('contractVerification/stopContractVerification')
          store.dispatch('setLoading', false)

          const stepToRestore = activeStep || 'availability'
          const routeName = store.state.internet5g.stepper[stepToRestore]?.routeName ?? 'Availability'

          if (route.name !== routeName) {
            return router.push({ name: routeName, replace: true })
          }
          return
        }
      } catch (e) {
        if (e instanceof SyntaxError) {
          localStorage.removeItem('weclix_internet_5g_checkout')
        }
      }
    }

    if (store.getters.getIs5g && !hasNewPlanSelectionIntent) return

    if ((!selectedCity || !selectedPlan || !selectedPlanType) && localStorage.getItem('session_uuid')) {

      try {

        const session = await store.dispatch(
          'fetchSelectedPlanBySession',
          localStorage.getItem('session_uuid')
        )

        store.dispatch('inputFormObjectValues', {
          name: session.prospect?.name,
          email: session.prospect?.email,
          phone: session.prospect?.cellphone || session.prospect?.phone,
          address_id: session.prospect?.addresses?.address_id,
          address_name: session.prospect?.addresses?.address_name,
          address_number: session.prospect?.addresses?.number,
          address_complement: session.prospect?.addresses?.complement,
          cpf: session.prospect?.cpf,
          rg: session.prospect?.rg,
          mother_name: session.prospect?.mother_name,
          birth_date: session.prospect?.birth_date,
          cnpj: session.prospect?.cnpj,
          company_name: session.prospect?.company_name,
          state_registration: session.prospect?.state_registration,
          municipal_registration: session.prospect?.municipal_registration,
          fantasy_name: session.prospect?.fantasy_name,
          terms: false,
          payment_day_of_month: session.prospect?.payment_day_of_month,
          code: session.promo_code?.code,
          day: session.prospect?.day,
          period: session.prospect?.period,
          home_type: session.prospect?.home_type,
          observation: session.prospect?.observation,
          serviceContactType: ''
        })

        const currentStep = session.status === 'new' ? 'viability' : session.status

        if (currentStep) {
          await store.dispatch('completeStepsUntil', currentStep)
          store.dispatch('setActiveStep', currentStep)

          const storedStatus = await store.dispatch('contractVerification/restoreContractStatusFromStorage')
          if (storedStatus?.status === 'under_analysis') {
            const sessionUuid = localStorage.getItem('session_uuid')
            if (sessionUuid) {
              store.dispatch('contractVerification/startContractVerification', sessionUuid)
            }
          }

          return router.push({
            name: store.state.stepper.stepper[currentStep].routeName,
            replace: true
          })
        }

        return router.push({ name: 'Availability', replace: true })

      } catch (e) {
        localStorage.clear()
        console.log('houve um erro->', e)
      }
    } else {     

      let visitorId = ''

      // Get the visitor identifier when you need it.
      await fpPromise
        .then(fp => fp.get())
        .then(result => visitorId = result.visitorId)
      
      await store.dispatch('fetchSelectedPlan', { selectedCity, selectedPlan, selectedPlanType, visitorId, target, promoCode, affiliate })

    }

    //should always start on Availability page, doesn't matter where the user came from since it is the first load
    
    router.push({ name: 'Availability', replace: true })

  }
})


</script>
